/**
 * Perundingan "tarik-untuk-muat-ulang" dengan pembungkus APK Android
 * (31 Agustus 2026, dilaporkan lapangan).
 *
 * APK membungkus WebView-nya dengan `SwipeRefreshLayout`, dan widget itu
 * memutuskan boleh-tidaknya menyambar seretan dengan satu pertanyaan:
 * *"masih ada yang bisa digulung ke atas?"* — yang ditanyakan **kepada
 * WebView**. Di aplikasi ini jawabannya selalu "tidak": cangkangnya
 * `h-screen overflow-hidden` dan yang benar-benar menggulung selalu `<div>` di
 * dalam halaman, jadi gulungan dokumen WebView abadi di angka nol. Akibatnya
 * seretan ke bawah **di mana pun, dalam keadaan apa pun** dibaca sebagai
 * tarik-untuk-muat-ulang.
 *
 * Yang terasa: *"di dashboard kalo 1×4 gw scroll ke bawah sampe abis, mau balik
 * keatas swipe down, malah trigger refresh page."* Dan muat ulang di sini bukan
 * gangguan kecil — seluruh aliran MJPEG halaman itu putus lalu disambung ulang
 * (§D3.4).
 *
 * Jadi yang menjawab pertanyaan itu dipindahkan ke halaman, satu-satunya pihak
 * yang tahu apa yang sedang digulung. Bentuknya sama dengan kontrak D-pad TV
 * (`lib/panahTv.ts`): APK yang menyediakan, halaman yang mengisi — di sana lewat
 * `window.__lensaPanah`, di sini lewat objek `LensaAndroid` yang disuntikkan
 * `addJavascriptInterface`.
 *
 * Di browser biasa objek itu tidak ada dan seluruh berkas ini tidak melakukan
 * apa-apa: Chrome sudah menjawab pertanyaannya sendiri dengan benar.
 */

interface JembatanAndroid {
  /** `true` = tidak ada yang tergulung di bawah jari; tarikan boleh jadi muat ulang. */
  setDiPuncak?: (diPuncak: boolean) => void;
}

function jembatan(): JembatanAndroid | undefined {
  const w = window as unknown as { LensaAndroid?: JembatanAndroid };
  return typeof w.LensaAndroid?.setDiPuncak === 'function' ? w.LensaAndroid : undefined;
}

/**
 * Elemen terdekat yang benar-benar menggulung — dari `el` naik ke atas.
 *
 * Dua syarat, dan keduanya perlu: isinya memang lebih tinggi daripada
 * kotaknya, **dan** `overflow-y` memang mengizinkan gulungan. Yang pertama saja
 * akan menunjuk elemen `overflow: hidden` yang isinya kebetulan meluap; yang
 * kedua saja akan menunjuk kotak `overflow-y: auto` yang isinya masih muat —
 * dan kotak yang tidak bisa digulung tidak boleh membatalkan muat ulang.
 */
function penggulung(el: Element | null): Element | null {
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (n.scrollHeight <= n.clientHeight + 1) continue;
    const oy = getComputedStyle(n).overflowY;
    if (oy === 'auto' || oy === 'scroll') return n;
  }
  return null;
}

/**
 * Wilayah yang mengurus sentuhannya sendiri menolak muat ulang, walau tidak ada
 * yang bisa digulung di sana.
 *
 * Layar penuh grid, viewer satu kamera, dan kotak video Playback tidak punya
 * gulungan sama sekali — jawaban jujurnya "di puncak", dan itu berarti setiap
 * sapuan menurun di atas gambar berakhir sebagai muat ulang. Padahal justru di
 * sanalah muat ulang paling mahal: layar penuhnya bongkar, seluruh aliran
 * disambung ulang, dan posisi pemutaran hilang. Sapuan menurun di wilayah itu
 * juga bukan gestur kosong — ia bagian dari cubit dan geser gambar ber-zoom.
 *
 * Ditandai dengan atribut, bukan daftar pemilih di berkas ini: yang tahu sedang
 * memegang sentuhan adalah komponennya sendiri, sama seperti `data-kendali`
 * pada zoom (§G10.6) dan `data-panah` pada remote TV.
 */
const TANPA_MUAT_ULANG = '[data-tanpa-muat-ulang]';

/**
 * Pasang perundingannya. Dipanggil sekali saat aplikasi start; aman dipanggil
 * di browser mana pun.
 */
export function pasangMuatUlangAndroid(): void {
  const api = jembatan();
  if (!api?.setDiPuncak) return;

  let terakhir: boolean | null = null;
  const lapor = (diPuncak: boolean) => {
    // Kejadian gulung datang puluhan kali per detik; menyeberangi jembatan JNI
    // tiap kali percuma. Yang dikirim hanya perubahan jawaban.
    if (diPuncak === terakhir) return;
    terakhir = diPuncak;
    api.setDiPuncak!(diPuncak);
  };

  /*
   * Jawaban paling tepat diberikan saat jari **mendarat**: yang menentukan
   * bukan "ada sesuatu yang sedang tergulung di halaman ini" melainkan "yang
   * ada di bawah jari ini bisa digulung ke atas atau tidak". Menarik ke bawah
   * dari bilah kendali — yang bukan penggulung — tetap berarti muat ulang,
   * persis seperti di browser.
   *
   * `touchstart` mendahului keputusan `SwipeRefreshLayout`, yang baru diambil
   * sesudah jarinya menempuh jarak ambang (±8dp); jeda itu jauh lebih panjang
   * daripada satu penangan sesingkat ini.
   */
  document.addEventListener(
    'touchstart',
    (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (t?.closest(TANPA_MUAT_ULANG)) {
        lapor(false);
        return;
      }
      const p = penggulung(t);
      lapor(!p || p.scrollTop <= 0);
    },
    { capture: true, passive: true },
  );

  /*
   * Dan diperbarui selama gulungan berjalan, supaya seretan yang **sampai** ke
   * puncak boleh diteruskan jadi muat ulang tanpa mengangkat jari dulu — itu
   * yang dikerjakan browser, dan menirunya membuat gestur ini terasa sama di
   * APK dan di Chrome.
   *
   * Fase tangkap: kejadian `scroll` pada elemen tidak menggelembung, jadi
   * pendengar di `document` hanya kebagian lewat jalur ini.
   */
  document.addEventListener(
    'scroll',
    (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return; // gulungan dokumen; bukan urusan sini
      lapor(t.scrollTop <= 0);
    },
    { capture: true, passive: true },
  );
}
